import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Header,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderUserMenu,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  MainScrollArea,
  MainHeader,
  Main,
  MainActions,
  MainHeaderBackButton,
} from '@/app/ui/components/main';
import { H1 } from '@/app/ui/components/heading';
import { getProjectDetailsQuery, useGetProjectsInsights } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { Building06Icon, Chart01Icon } from '@hugeicons/core-free-icons';
import { useState } from 'react';
import { Spinner } from '@/app/ui/components/spinner';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/app/ui/components/empty';
import { Button } from '@/app/ui/components/button';
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from '@/app/ui/components/pagination';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectTriggerIcon,
  SelectValue,
} from '@/app/ui/components/select';
import { Insight } from '@/app/types/models';
import NewInsightItem from '@/app/components/features/insights/NewInsightItem';
import NewInsightModal from '@/app/components/features/insights/NewInsightModal';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import {
  Menu,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  MenuSubmenuTrigger,
  Submenu,
} from '@/app/ui/components/menu';
import { HugeiconsIcon } from '@hugeicons/react';
import { InsightsIcon } from '@/app/components/shared/EmptyIcons';
import { Group, GroupItem, GroupSeparator } from '@/app/ui/components/group';
import { MainActionsScrollArea } from '@/app/ui/components/main';
import { CancelIcon } from '@hugeicons/core-free-icons';
import { Icons } from '@/app/ui/components/icons';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/_authed/project/$projectId/_layout/insights')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const tCommon = useTranslations('general');
  const tTable = useTranslations('components.table');
  const tFilters = useTranslations('pages.projectDetails.insights.filters');
  const { projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const { data, isLoading, pageCount, page, setPage, pageSize, setPageSize, handleFiltersChange } =
    useGetProjectsInsights(projectId);

  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [selectedEvalType, setSelectedEvalType] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');

  const filtersNumber = (selectedEvalType !== 'all' ? 1 : 0) + (selectedSource !== 'all' ? 1 : 0);

  const handleEvalTypeChange = (value: string) => {
    setSelectedEvalType(value);
    applyFilters(value, selectedSource);
  };

  const handleSourceChange = (value: string) => {
    setSelectedSource(value);
    applyFilters(selectedEvalType, value);
  };

  const applyFilters = (evalType: string, source: string) => {
    const newFilters: Record<string, string[]> = {};

    if (evalType !== 'all') {
      newFilters.filterByEvaluationType = [evalType];
    } else {
      newFilters.filterByEvaluationType = [];
    }

    if (source === 'overviews') {
      newFilters.filterByChat = ['false'];
    } else {
      newFilters.filterByChat = [];
    }

    handleFiltersChange(newFilters);
  };

  const handleResetFilters = () => {
    setSelectedEvalType('all');
    setSelectedSource('all');
    applyFilters('all', 'all');
  };

  if (isLoading) return <Spinner kind="inset" />;

  return (
    <Main>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to={'/'}>
                <HugeiconsIcon icon={Building06Icon} />
                {t('pages.homepage.breadcrumbs.projects')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink to={'/project/$projectId'} params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={Chart01Icon} />
                {t('pages.insights.title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton />
            {t('pages.insights.title')}
          </H1>
        </MainHeader>
        <MainActions className="justify-start">
          <Menu>
            <MenuTrigger
              render={
                <Button variant="ghost" size="sm" kind="with-icon" className="max-lg:ml-1.75">
                  <Icons.filters />
                  {tCommon('filter')}
                  {filtersNumber > 0 && <span className="tabular-nums">[{filtersNumber}]</span>}
                </Button>
              }
            />
            <MenuPopup align="start">
              <Submenu>
                <MenuSubmenuTrigger>{tFilters('typeOfEvaluation')}</MenuSubmenuTrigger>
                <MenuPopup>
                  <MenuRadioGroup value={selectedEvalType} onValueChange={handleEvalTypeChange}>
                    <MenuRadioItem value="all">{tCommon('all')}</MenuRadioItem>
                    <MenuRadioItem value={EvaluationTypeLabelsEnum.CONTRACT}>
                      {tFilters('Contract')}
                    </MenuRadioItem>
                    <MenuRadioItem value={EvaluationTypeLabelsEnum.TENDER_PACKAGE}>
                      {tFilters('TenderPackage')}
                    </MenuRadioItem>
                  </MenuRadioGroup>
                </MenuPopup>
              </Submenu>
              <Submenu>
                <MenuSubmenuTrigger>{tFilters('source')}</MenuSubmenuTrigger>
                <MenuPopup>
                  <MenuRadioGroup value={selectedSource} onValueChange={handleSourceChange}>
                    <MenuRadioItem value="all">{tCommon('all')}</MenuRadioItem>
                    <MenuRadioItem value="overviews">{tFilters('overviews')}</MenuRadioItem>
                  </MenuRadioGroup>
                </MenuPopup>
              </Submenu>
            </MenuPopup>
          </Menu>
          {(selectedEvalType !== 'all' || selectedSource !== 'all') && (
            <MainActionsScrollArea>
              {selectedEvalType !== 'all' && (
                <Group>
                  <Menu>
                    <GroupItem
                      render={
                        <MenuTrigger render={<Button size="sm" variant="secondary" />}>
                          {tFilters(
                            selectedEvalType === EvaluationTypeLabelsEnum.CONTRACT
                              ? 'Contract'
                              : 'TenderPackage',
                          )}
                        </MenuTrigger>
                      }
                    />
                    <MenuPopup align="start">
                      <MenuRadioGroup value={selectedEvalType} onValueChange={handleEvalTypeChange}>
                        <MenuRadioItem value={EvaluationTypeLabelsEnum.CONTRACT}>
                          {tFilters('Contract')}
                        </MenuRadioItem>
                        <MenuRadioItem value={EvaluationTypeLabelsEnum.TENDER_PACKAGE}>
                          {tFilters('TenderPackage')}
                        </MenuRadioItem>
                      </MenuRadioGroup>
                    </MenuPopup>
                  </Menu>
                  <GroupSeparator />
                  <GroupItem
                    render={
                      <Button
                        size="sm"
                        kind="icon"
                        variant="secondary"
                        onClick={() => handleEvalTypeChange('all')}
                      />
                    }
                  >
                    <HugeiconsIcon icon={CancelIcon} />
                  </GroupItem>
                </Group>
              )}
              {selectedSource !== 'all' && (
                <Group>
                  <Menu>
                    <GroupItem
                      render={
                        <MenuTrigger render={<Button size="sm" variant="secondary" />}>
                          {tFilters('overviews')}
                        </MenuTrigger>
                      }
                    />
                    <MenuPopup align="start">
                      <MenuRadioGroup value={selectedSource} onValueChange={handleSourceChange}>
                        <MenuRadioItem value="overviews">{tFilters('overviews')}</MenuRadioItem>
                      </MenuRadioGroup>
                    </MenuPopup>
                  </Menu>
                  <GroupSeparator />
                  <GroupItem
                    render={
                      <Button
                        size="sm"
                        kind="icon"
                        variant="secondary"
                        onClick={() => handleSourceChange('all')}
                      />
                    }
                  >
                    <HugeiconsIcon icon={CancelIcon} />
                  </GroupItem>
                </Group>
              )}
            </MainActionsScrollArea>
          )}
        </MainActions>

        {!data?.length && filtersNumber > 0 ? (
          <Empty>
            <EmptyMedia>
              <InsightsIcon />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>{tCommon('filters.noResults')}</EmptyTitle>
              <EmptyDescription>{tCommon('filters.changeFilters')}</EmptyDescription>
              <Button onClick={handleResetFilters} variant="secondary">
                {tCommon('filters.resetFilters')}
              </Button>
            </EmptyContent>
          </Empty>
        ) : !data?.length ? (
          <Empty>
            <EmptyMedia>
              <InsightsIcon />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>{t('pages.projectDetails.insights.listEmpty.heading')}</EmptyTitle>
              <EmptyDescription>
                {t('pages.projectDetails.insights.listEmpty.subheading')}
              </EmptyDescription>
            </EmptyContent>
          </Empty>
        ) : (
          <>
            <div className="px-x py-4 space-y-4">
              {data?.map((insight) => (
                <NewInsightItem
                  key={insight.statementId}
                  insight={insight}
                  onView={setSelectedInsight}
                />
              ))}
            </div>

            {pageCount > 1 && (
              <Pagination className="mt-6 max-md:flex-col max-md:items-start">
                <div className="flex items-center gap-3 max-sm:justify-between max-sm:w-full">
                  <p className="text-muted max-sm:mr-auto text-sm">
                    {tTable('pagination.rowsPerPage')}
                  </p>
                  <Select
                    value={pageSize}
                    onValueChange={(value) => {
                      if (value) setPageSize(value as number);
                    }}
                  >
                    <SelectTrigger
                      aria-label="Select page size"
                      className="w-fit min-w-none"
                      size="sm"
                    >
                      <SelectValue />
                      <SelectTriggerIcon />
                    </SelectTrigger>
                    <SelectPopup>
                      {[10, 20, 30, 40, 50].map((size) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                </div>
                <div className="flex items-center gap-3 max-sm:justify-between max-sm:w-full">
                  <p className="text-muted">
                    {tTable('pagination.page')} {page} {tTable('pagination.of')} {pageCount}
                  </p>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationFirst disabled={page === 1} onClick={() => setPage(1)} />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationPrevious disabled={page === 1} onClick={() => setPage(page - 1)} />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        disabled={page === pageCount}
                        onClick={() => setPage(page + 1)}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLast
                        disabled={page === pageCount}
                        onClick={() => setPage(pageCount)}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </div>
              </Pagination>
            )}
          </>
        )}

        <NewInsightModal insight={selectedInsight} onClose={() => setSelectedInsight(null)} />
      </MainScrollArea>
    </Main>
  );
}
